import { discardPhotoDraft, loadPhotoDraft, photoDraftKey, samePhotoSnapshot } from './deliveryPhotoDraftPersistence.js';
import { withDeliveryCopyOperation } from './deliveryCopyOperation.js';
import {
  acquireDeliveryAction, clearDeliveryWorkspace, createDeliveryWorkspace, getDeliveryAction,
  getDeliveryWorkspace, hasDeliveryWorkspaceWork, releaseDeliveryAction,
} from './deliveryDraftStore.js';

// Local disposal is not delivery authority. Never call HTTP or touch legacy
// outbox copies here, including when an order is closed, past or reassigned.
function checkSelection(principal, selection, isCurrent) {
  photoDraftKey(principal, selection?.orderId, selection?.kind);
  if (!isCurrent()) throw new Error('The selection is no longer active. Nothing was discarded.');
  if (selection.status !== 'draft' || typeof selection.revision !== 'string' || !selection.revision) {
    throw new Error('Check the saved attempt receipt before retiring this work.');
  }
  if (getDeliveryAction({ principalKey: principal, orderId: selection.orderId })) {
    throw new Error('This order has another operation in progress. Nothing was discarded.');
  }
  return getDeliveryWorkspace(principal, selection.orderId, selection.kind);
}

const changed = () => new Error('This delivery changed. Open saved work to review it, then try deleting again. Nothing was deleted.');

export async function preparePreparedPhotoDeletion(principal, selection, isCurrent) {
  const work = checkSelection(principal, selection, isCurrent);
  const saved = await loadPhotoDraft(principal, selection.orderId, selection.kind);
  if (checkSelection(principal, selection, isCurrent) !== work) throw changed();
  if (!saved) return null;
  if (saved.revision !== selection.revision) throw new Error('The saved photo draft changed. Refresh and review it again.');
  if (saved.recovery?.attempt) throw new Error('Check the saved attempt receipt before retiring this work.');
  // Closing a saved form retains its workspace. Delete that identical copy too,
  // but never treat a matching revision alone as proof that edits are saved.
  if (work && (work.savedMediaRemoval || (hasDeliveryWorkspaceWork(work)
      && (work.photoDraftRevision !== saved.revision || !await samePhotoSnapshot(work, saved))))) throw changed();
  if (checkSelection(principal, selection, isCurrent) !== work) throw changed();
  return { principal, orderId: selection.orderId, kind: selection.kind, revision: selection.revision, status: 'draft',
    ...(work ? { workspace: { id: work.id, revision: work.revision } } : {}) };
}

export async function deletePreparedPhotoDraft(principal, selection, isCurrent) {
  if (selection?.principal !== principal) throw new Error('The saved copy belongs to a different account.');
  const previous = checkSelection(principal, selection, isCurrent);
  if ((previous?.id ?? null) !== (selection.workspace?.id ?? null)
      || (previous?.revision ?? null) !== (selection.workspace?.revision ?? null)) throw changed();
  // Reuse the order action owner; do not race a form preparing or submitting
  // the other operation. An untouched temporary shell carries no photo data.
  const work = previous || createDeliveryWorkspace(principal, selection.orderId, selection.kind, {});
  const lease = acquireDeliveryAction(work, 'discard-saved-photo-draft');
  if (!lease) {
    if (!previous) clearDeliveryWorkspace(work);
    throw new Error('This order has another operation in progress. Nothing was discarded.');
  }
  try {
    const stillOwns = () => isCurrent() && getDeliveryWorkspace(principal, selection.orderId, selection.kind) === work
      && getDeliveryAction(work) === lease;
    await withDeliveryCopyOperation(selection.orderId, () => {
      if (!stillOwns()) throw new Error('The selection is no longer active. Nothing was discarded.');
      return discardPhotoDraft(principal, selection.orderId, selection.kind, selection.revision, {
        unsentOnly: true, isCurrent: stillOwns,
      });
    }, { requireCrossContext: true });
    // Only clear after durable deletion commits. The lease and frozen consent
    // protect newer edits; keeping this matching workspace would revive the draft.
    clearDeliveryWorkspace(work, lease);
    return 'deleted';
  } finally {
    if (!previous) clearDeliveryWorkspace(work, lease);
    releaseDeliveryAction(lease);
  }
}

export const preparedPhotoDeletion = (principal, record) => ({
  prepare: (_orderId, isCurrent) => preparePreparedPhotoDeletion(principal, record, isCurrent),
  remove: (selection, isCurrent) => deletePreparedPhotoDraft(principal, selection, isCurrent),
  button: 'Discard unsent photo draft',
  title: 'Discard this unsent photo draft from this device?',
  description: 'Photos and details in this unsent draft will be permanently lost. Camera-roll originals, server photos, other drafts and legacy saved copies will not be changed.',
  success: 'Unsent photo draft discarded from this device.',
});
